import axios from 'axios';

const getApiUrl = () => {
    // В приоритете всегда переменная окружения
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    
    // Для разработки на локальной машине
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:8080';
    }
    
    // Если переменная не задана и мы не на localhost, 
    // используем текущий хост как основу (предполагая, что API на том же домене или поддомене api)
    return `${window.location.protocol}//api.${window.location.hostname}`;
};

const API_URL = getApiUrl();

// Create axios instance with default config
const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    },
    withCredentials: true
});

// Add request interceptor for token handling
api.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API methods
export const authAPI = {
    loginWithGoogle: async (credentialResponse) => {
        try {
            
            const response = await api.post('/auth/google', {
                credential: credentialResponse.credential
            });
            
            
            return response;
        } catch (error) {
            console.error('authAPI.loginWithGoogle: Error logging in with Google:', error);
            throw error;
        }
    },

    logout: async () => {
        try {
            await api.post('/auth/logout');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        } catch (error) {
            console.error('Error logging out:', error);
            // Still remove items even if API call fails
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            throw error;
        }
    },

    getCurrentUser: async () => {
        try {
            const response = await api.get('/auth/me');
            return response.data;
        } catch (error) {
            console.error('Error getting current user:', error);
            throw error;
        }
    },

    updateProfile: async (profileData) => {
        try {
            
            // Проверяем наличие нужных данных
            if (!profileData) {
                throw new Error('No profile data provided');
            }
            
            // Сохраняем точное время отправки запроса для отладки
            const requestTime = new Date().toISOString();
            
            const response = await api.put('/auth/profile', profileData);
            
            // Сохраняем точное время получения ответа
            const responseTime = new Date().toISOString();
            
            // Обновляем данные пользователя в localStorage
            if (response.data?.user) {
                // Получаем текущие данные пользователя из localStorage
                const currentUserStr = localStorage.getItem('user');
                let currentUser = {};
                
                if (currentUserStr) {
                    try {
                        currentUser = JSON.parse(currentUserStr);
                    } catch (e) {
                        console.error('Error parsing user data from localStorage', e);
                    }
                }
                
                // Обновляем данные пользователя, сохраняя существующие поля, 
                // которые не были изменены
                const updatedUser = {
                    ...currentUser,
                    ...response.data.user
                };
                
                localStorage.setItem('user', JSON.stringify(updatedUser));
                
                // Создаем и диспатчим пользовательское событие для обновления состояния приложения
                const event = new CustomEvent('user-updated', { 
                    detail: { user: updatedUser } 
                });
                document.dispatchEvent(event);
            } else {
                console.warn('Response does not contain user data:', response.data);
            }
            
            return response.data;
        } catch (error) {
            console.error('Error updating profile:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error('Error updating profile');
        }
    },

    deleteAccount: async (confirmationText) => {
        try {
            console.log('Attempting to delete account...');
            
            const response = await api.delete('/auth/account', {
                data: { confirmationText }
            });
            
            // Очищаем все данные пользователя
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            
            // Диспатчим событие о выходе пользователя
            const event = new CustomEvent('user-logged-out');
            document.dispatchEvent(event);
            
            return response.data;
        } catch (error) {
            console.error('Error deleting account:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error('Error deleting account');
        }
    },

    submitInteractionIdeaFeedback: async (idea, feedback) => {
        try {
            const response = await api.post('/auth/interaction-idea-feedback', {
                idea,
                feedback
            });
            return response.data;
        } catch (error) {
            console.error('Error submitting interaction idea feedback:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error('Error submitting feedback');
        }
    }
};

// Contacts API methods
export const contactsAPI = {
    getContacts: async (includeArchived = false) => {
        try {
            const params = includeArchived ? { archived: 'true' } : {};
            const response = await api.get('/contacts', { params });
            return response;
        } catch (error) {
            console.error('Error fetching contacts:', error);
            throw error;
        }
    },

    getContactsSummary: async (includeArchived = false) => {
        try {
            const params = includeArchived ? { archived: 'true' } : {};
            const response = await api.get('/contacts/summary', { params });
            return response;
        } catch (error) {
            console.error('Error fetching contacts summary:', error);
            throw error;
        }
    },

    getContact: async (contactId) => {
        try {
            const response = await api.get(`/contacts/${contactId}`);
            return response;
        } catch (error) {
            console.error('Error fetching contact:', error);
            throw error;
        }
    },

    addContact: async (data) => {
        try {
            
            // Проверяем наличие обязательных полей
            if (!data.name || data.name.trim() === '') {
                console.error('API: Missing required field: name');
                throw new Error('Contact name is required');
            }
            
            const response = await api.post('/contacts', data);
            
            if (!response.data) {
                console.error('API: Server response missing data:', response);
                throw new Error('Invalid server response');
            }
            
            return response;
        } catch (error) {
            console.error('API: Error adding contact:', error);
            
            if (error.response) {
                console.error('API: Server error status:', error.response.status);
                console.error('API: Server error message:', error.response.data?.message || error.response.statusText);
                throw new Error(error.response.data?.message || 'Server error occurred');
            } else if (error.request) {
                console.error('API: No response received from server');
                throw new Error('No response from server. Please check your connection.');
            }
            
            throw error;
        }
    },

    addEncounter: async (data) => {
        try {
            const response = await api.post('/contacts/encounter', data);
            return response;
        } catch (error) {
            console.error('API: Error adding encounter:', error);
            if (error.response) {
                throw new Error(error.response.data?.message || 'Server error occurred');
            }
            throw error;
        }
    },

    editContact: async (id, data) => {
        try {
            // Проверяем, что ID определен
            if (!id) {
                console.error('API: Cannot edit contact - Missing contact ID');
                throw new Error('Contact ID is required for editing');
            }
            
            
            const response = await api.put(`/contacts/${id}`, data);
            
            // Проверяем, что сервер вернул данные с ID
            if (!response.data || !response.data._id) {
                console.error('API: Server response missing contact ID after edit:', response);
            } else {
            }
            
            return response;
        } catch (error) {
            console.error(`API: Error editing contact with ID ${id}:`, error);
            console.error('API: Error details:', error.response?.data);
            
            // Логируем дополнительные детали ошибки
            if (error.response) {
                console.error('API: Server error status:', error.response.status);
                console.error('API: Server error message:', error.response.statusText);
            } else if (error.request) {
                console.error('API: No response received from server');
            } else {
                console.error('API: Error setting up request:', error.message);
            }
            
            throw error;
        }
    },

    deleteContact: async (contactId) => {
        try {
            const response = await api.delete(`/contacts/${contactId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting contact:', error);
            throw error;
        }
    },

    archiveContact: async (contactId) => {
        try {
            const response = await api.post(`/contacts/${contactId}/archive`);
            return response;
        } catch (error) {
            console.error('Error archiving contact:', error);
            throw error;
        }
    },

    unarchiveContact: async (contactId) => {
        try {
            const response = await api.post(`/contacts/${contactId}/unarchive`);
            return response;
        } catch (error) {
            console.error('Error unarchiving contact:', error);
            throw error;
        }
    },

    addInteraction: async (contactId, data) => {
        try {
            const response = await api.post(`/contacts/${contactId}/interactions`, data);
            return response;
        } catch (error) {
            console.error('Error adding interaction:', error);
            throw error;
        }
    },

    deleteInteraction: async (contactId, interactionId) => {
        try {
            const response = await api.delete(`/contacts/${contactId}/interactions/${interactionId}`);
            return response;
        } catch (error) {
            console.error('Error deleting interaction:', error);
            throw error;
        }
    },

    updateInteraction: async (contactId, interactionId, data) => {
        try {
            // Use a URL path that matches your backend routes
            // Remove /api if your backend routes don't include it
            const response = await api.put(`/contacts/${contactId}/interactions/${interactionId}`, data);
            return response;
        } catch (error) {
            console.error('Error updating interaction:', error);
            throw error;
        }
    },

    searchContacts: async (query) => {
        try {
            const response = await api.get(`/contacts/search?q=${encodeURIComponent(query)}`);
            return response;
        } catch (error) {
            console.error('Error searching contacts:', error);
            throw error;
        }
    },

    regenerateSummary: async (contactId) => {
        try {
            const response = await api.post(`/contacts/${contactId}/regenerate-summary`);
            return response.data;
        } catch (error) {
            console.error('Error regenerating summary:', error);
            throw error;
        }
    },

    // Экспорт контактов в CSV
    exportContactsCSV: async () => {
        try {
            const response = await api.get('/csv/export', {
                responseType: 'blob', // Важно для скачивания файлов
            });
            return response;
        } catch (error) {
            console.error('Error exporting contacts to CSV:', error);
            throw error;
        }
    },

    // Импорт контактов из CSV
    importContactsCSV: async (csvFile) => {
        try {
            const formData = new FormData();
            formData.append('csvFile', csvFile);
            
            const response = await api.post('/csv/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response;
        } catch (error) {
            console.error('Error importing contacts from CSV:', error);
            throw error;
        }
    }
};

export const opportunitiesAPI = {
    getOpportunities: async () => {
        const response = await api.get('/opportunities');
        return response.data;
    },

    createOpportunity: async (data) => {
        const response = await api.post('/opportunities', data);
        return response.data;
    },

    updateOpportunity: async (id, data) => {
        const response = await api.put(`/opportunities/${id}`, data);
        return response.data;
    },

    deleteOpportunity: async (id) => {
        const response = await api.delete(`/opportunities/${id}`);
        return response.data;
    }
};

// Chat Assistant API methods
export const chatAPI = {
    analyzeContacts: async (contacts, message) => {
        try {
            const response = await api.post('/chat/analyze', { contacts, message });
            return response.data;
        } catch (error) {
            console.error('Error analyzing contacts with AI:', error);
            if (error.response) {
                console.error('Server error details:', {
                    status: error.response.status,
                    data: error.response.data
                });
            }
            throw error;
        }
    },

    transcribeAudio: async (audioBlob) => {
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');

            const response = await api.post('/chat/transcribe', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error transcribing audio:', error);
            throw error;
        }
    }
};

// Telegram API methods
export const telegramAPI = {
    generateLinkToken: async () => {
        try {
            const response = await api.post('/telegram/link');
            return response.data;
        } catch (error) {
            console.error('Error generating link token:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    },

    unlinkAccount: async () => {
        try {
            const response = await api.post('/telegram/unlink');
            return response.data;
        } catch (error) {
            console.error('Error unlinking account:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    },

    getTelegramStatus: async () => {
        try {
            const response = await api.get('/telegram/status');
            return response.data;
        } catch (error) {
            console.error('Error getting Telegram status:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    },

    updateTelegramSettings: async (birthdays, contactReminders, timezone, notificationTime, nudgeFrequency) => {
        try {
            const body = {
                birthdays,
                contactReminders
            };

            if (timezone !== undefined) {
                body.timezone = timezone;
            }

            if (notificationTime !== undefined) {
                body.notificationTime = notificationTime;
            }

            if (nudgeFrequency !== undefined) {
                body.nudgeFrequency = nudgeFrequency;
            }

            const response = await api.put('/telegram/settings', body);
            return response.data;
        } catch (error) {
            console.error('Error updating Telegram settings:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    }
};

// MCP token API methods
export const mcpTokenAPI = {
    listTokens: async () => {
        try {
            const response = await api.get('/mcp/tokens');
            return response.data;
        } catch (error) {
            console.error('Error listing MCP tokens:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    },

    generateToken: async ({ name, scopes, expiresInDays }) => {
        try {
            const response = await api.post('/mcp/tokens', {
                name,
                scopes,
                expiresInDays
            });
            return response.data;
        } catch (error) {
            console.error('Error generating MCP token:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    },

    revokeToken: async (id) => {
        try {
            const response = await api.delete(`/mcp/tokens/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error revoking MCP token:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    }
};

// MCP OAuth API methods
export const mcpOAuthAPI = {
    getRequest: async (requestId) => {
        try {
            const response = await api.get(`/mcp/oauth/requests/${requestId}`);
            return response.data;
        } catch (error) {
            console.error('Error loading MCP OAuth request:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    },

    approveRequest: async (requestId) => {
        try {
            const response = await api.post(`/mcp/oauth/requests/${requestId}/approve`);
            return response.data;
        } catch (error) {
            console.error('Error approving MCP OAuth request:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    },

    denyRequest: async (requestId) => {
        try {
            const response = await api.post(`/mcp/oauth/requests/${requestId}/deny`);
            return response.data;
        } catch (error) {
            console.error('Error denying MCP OAuth request:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw error;
        }
    }
};

export default api;
